import {
  AlertCircleIcon,
  PaperclipIcon,
  UploadIcon,
  XIcon,
  DownloadIcon,
} from "lucide-react";
import { FileWithPreview, formatBytes, useFileUpload } from "@/hooks/use-file-upload";
import { Button as ShadButton } from "@/components/ui/button";
import React, { SetStateAction, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { t } from "i18next";

interface ExistingFile {
  id: number;
  nomeArquivo: string;
  pathArquivo: string;
}

interface UploadFileProps {
  setFiles: React.Dispatch<SetStateAction<FileWithPreview[]>>;
  onDownloadClick: (fileId: number, fileName: string) => void;
  onDeleteClick: (fileId: number, fileName: string) => void;
  maxSize?: number;
  accept?: string;
  existingFiles?: ExistingFile[];
  classProps?: string;
  clearTrigger?: boolean;
  onClearDone?: () => void;
}

const POPULAR_FILE_TYPES = [
  'jpeg',
  'jpg',
  'png',
  'pdf',
  'xlsx',
  'docs',
  'docx',
  'csv',
  'txt',
].map(ext => `.${ext}`).join(','); 

const UploadFile: React.FC<UploadFileProps> = ({
  maxSize = 100 * 1024 * 1024,
  onDownloadClick,
  onDeleteClick,
  accept = POPULAR_FILE_TYPES,
  existingFiles = [],
  setFiles,
  classProps,
  clearTrigger = false,
  onClearDone,
}) => {
  const [
    { files, isDragging, errors },
    {
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      removeFile,
      clearFiles,
      getInputProps,
    },
  ] = useFileUpload({
    maxSize,
    accept,
    initialFiles: [],
    multiple: true,
  });

  useEffect(() => {
    if (clearTrigger) {
      clearFiles();
      onClearDone?.();
    }
  }, [clearTrigger, clearFiles, onClearDone]);

  useEffect(() => {
    setFiles(files);
  }, [files]);

 const truncateFileName = (name: string, maxLength: number) => {
    if (name.length <= maxLength) {
      return name;
    }

    const lastDotIndex = name.lastIndexOf(".");
  
    if (lastDotIndex === -1) {
      return name.slice(0, maxLength - 3) + "...";
    }

    const baseName = name.slice(0, lastDotIndex);
    const extension = name.slice(lastDotIndex);

    const availableLength = maxLength - extension.length - 3;

    const truncatedBaseName = baseName.slice(0, Math.max(0, availableLength));

    return truncatedBaseName + "..." + extension;
 };

  return (
    <div className={twMerge("flex flex-col gap-2", classProps)}>
      <div
        role="button"
        onClick={openFileDialog}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        data-dragging={isDragging || undefined}
        className="border-input hover:bg-accent/50 data-[dragging=true]:bg-accent/50 has-[input:focus]:border-ring has-[input:focus]:ring-ring/50 flex min-h-40 flex-col items-center justify-center rounded-xl border border-dashed p-4 transition-colors has-disabled:pointer-events-none has-disabled:opacity-50 has-[input:focus]:ring-[3px]"
      >
        <input
          {...getInputProps({ multiple: true })}
          className="sr-only"
          aria-label="Upload file"
          type="file"
        />
        <div className="flex flex-col items-center justify-center text-center">
          <div
            className="bg-background mb-2 flex size-11 shrink-0 items-center justify-center rounded-full border"
            aria-hidden="true"
          >
            <UploadIcon className="size-4 opacity-60" />
          </div>
          <p className="text-muted-foreground text-xs">
            {t("uploadFileDialog.subtitle")} (max. {formatBytes(maxSize)})
          </p>
        </div>
      </div>

      {errors.length > 0 && (
        <div
          className="text-destructive flex items-center gap-1 text-xs"
          role="alert"
        >
          <AlertCircleIcon className="size-3 shrink-0" />
          <span>{errors[0]}</span>
        </div>
      )}
      
      {(existingFiles.length > 0 || files.length > 0) && (
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {/* SEÇÃO DE ARQUIVOS EXISTENTES CORRIGIDA */}
          {existingFiles.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-3 rounded-xl border px-4 py-2"
            >
              <PaperclipIcon className="size-4 shrink-0 opacity-60" />
              
              <p className="flex-1 min-w-0 truncate text-[13px] font-medium">
                {file.nomeArquivo}
              </p>
              
              <div className="flex shrink-0 items-center">
                <ShadButton
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-muted-foreground/80 hover:text-foreground -me-2 size-8 hover:bg-transparent"
                  onClick={() => onDownloadClick(file.id, file.nomeArquivo)}
                  aria-label="Download file"
                >
                  <DownloadIcon className="size-4" aria-hidden="true" />
                </ShadButton>
                <ShadButton
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-muted-foreground/80 hover:text-foreground -me-2 size-8 hover:bg-transparent"
                  onClick={() => onDeleteClick(file.id, file.nomeArquivo)}
                  aria-label="Remove file"
                >
                  <XIcon className="size-4" aria-hidden="true" />
                </ShadButton>
              </div>
            </div>
          ))}

          {/* SEÇÃO DE NOVOS ARQUIVOS CORRIGIDA */}
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-3 rounded-xl border border-dashed border-primario-500 px-4 py-2"
            >
              <PaperclipIcon className="size-4 shrink-0 opacity-60" />

              <p className="flex-1 min-w-0 truncate text-[13px] font-medium">
               {truncateFileName(file.file.name, 76)}{' '} <span className="text-primario-500">-   {t("uploadFileDialog.messages.newFile")}</span>
              </p>

              <div className="flex shrink-0 items-center">
                <ShadButton
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-muted-foreground/80 hover:text-foreground -me-2 size-8 hover:bg-transparent"
                  disabled
                  aria-label="Download file"
                >
                  <DownloadIcon className="size-4" aria-hidden="true" />
                </ShadButton>
                <ShadButton
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-muted-foreground/80 hover:text-foreground -me-2 size-8 hover:bg-transparent"
                  onClick={() => removeFile(file.id)}
                  aria-label="Remove file"
                >
                  <XIcon className="size-4" aria-hidden="true" />
                </ShadButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UploadFile;